import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatMessageProps } from '../../types/components';
import { Citations } from './Citations';
import { useAuth } from '../../context/AuthContext';

export const ChatMessage: React.FC<ChatMessageProps> = React.memo(({ message, isLoading = false, currentSessionId }) => {
  const { user } = useAuth();
  
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const isSystem = message.role === 'system';

  // Use fullContent if available, otherwise fall back to content
  const displayContent = message.getDisplayContent();
  const citations = message.getCitations();
  const annotations = message.getAnnotations();

  // Check if message contains wellbeing officer email or phone
  const wellbeingOfficerEmail = user?.clientConfig?.wellbeingOfficerEmail;
  const wellbeingOfficerPhone = user?.clientConfig?.wellbeingOfficerPhone;
  const bookingLink = user?.clientConfig?.bookingLink;
  
  const containsWellbeingEmail = wellbeingOfficerEmail && 
    displayContent.toLowerCase().includes(wellbeingOfficerEmail.toLowerCase());
  const containsWellbeingPhone = wellbeingOfficerPhone && 
    displayContent.includes(wellbeingOfficerPhone);
  
  const shouldShowBookingButton = bookingLink && (containsWellbeingEmail || containsWellbeingPhone);
  
  const handleBookingClick = () => {
    if (bookingLink) {
      // Open booking link directly in new tab since Outlook booking pages
      // cannot be embedded in iframes due to X-Frame-Options security headers
      window.open(bookingLink, '_blank', 'noopener,noreferrer');
    }
  };

  // Debug: Log raw assistant message data including citations and annotations
  // if (isAssistant) {
  //   console.log('Raw assistant message:', message);
  // }

  // Preprocess content to convert citations and emails to markdown links
  const processContentWithCitations = (content: string) => {
    let processedContent = content;
    
    // Process citations if annotations exist
    if (annotations.length > 0) {
      // Create a map of URL to citation index (1-based) for proper numbering
      const urlToCitationNumber = new Map<string, number>();
      citations.forEach((citation, index) => {
        urlToCitationNumber.set(citation.url, index + 1);
      });
      
      // Process annotations and replace with correctly numbered citations
      annotations.forEach((annotation: any) => {
        if (annotation.type === 'url_citation') {
          const citationText = annotation.text;
          const url = annotation.urlCitation.url;
          const title = annotation.urlCitation.title;
          
          // Find the correct citation number based on the URL's position in citations array
          const citationNumber = urlToCitationNumber.get(url);
          
          if (citationNumber) {
            // Create a simplified citation marker that our component can recognize
            const simpleCitation = `【${citationNumber}】`;
            
            // Replace citation with markdown link using correct citation number
            const markdownLink = `[${simpleCitation}](${url} "${title}")`;
            processedContent = processedContent.replace(citationText, markdownLink);
          }
        }
      });
    }
    
    // Convert email addresses to mailto links
    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
    processedContent = processedContent.replace(emailRegex, (email) => {
      return `[${email}](mailto:${email})`;
    });

    return processedContent;
  };

  // Helper function to convert numbers to superscript characters
  const toSuperscript = (num: string): string => {
    const superscriptMap: { [key: string]: string } = {
      '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
      '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹'
    };
    return num.split('').map(digit => superscriptMap[digit] || digit).join('');
  };

  // Custom components for ReactMarkdown to style citation links and emails
  const markdownComponents = {
    a: ({ href, title, children }: { href?: string; title?: string; children: React.ReactNode }) => {
      // Handle children as either string or array
      const childrenText = Array.isArray(children) ? children[0] : children;
      
      // Check if this is a citation link (contains 【】 pattern)
      const isCitation = typeof childrenText === 'string' && /【[^】]+】/.test(childrenText);
      
      if (isCitation) {
        // Extract the number from simplified citations like "【0】" -> "0"
        const citationMatch = typeof childrenText === 'string' ? childrenText.match(/【(\d+)】/) : null;
        const citationNumber = citationMatch ? citationMatch[1] : '1';
        
        return (
          <span
            className="inline-block text-rm-primary-700 font-medium mx-0.5"
            title={title}
          >
            {toSuperscript(citationNumber)}
          </span>
        );
      }
      
      // Check if this is an email link
      const isEmail = href?.startsWith('mailto:');
      
      if (isEmail) {
        return (
          <a
            href={href}
            className="text-rm-accent-600 hover:text-rm-accent-500 underline font-medium transition-colors"
            title={`Send email to ${children}`}
          >
            {children}
          </a>
        );
      }
      
      // Regular link styling
      return (
        <span
          className="text-rm-primary-700 font-semibold"
          title={title}
        >
          {children}
        </span>
      );
    },
    p: ({ children }: { children: React.ReactNode }) => {
      // Custom paragraph component to control spacing - remove all margins
      return <p className="m-0">{children}</p>;
    },
    br: () => {
      // Custom line break component
      return <br />;
    },
    // Tighten list spacing to avoid extra gaps from browser defaults
    ol: ({ children }: { children: React.ReactNode }) => (
      <ol className="m-0 pl-5 list-decimal">{children}</ol>
    ),
    ul: ({ children }: { children: React.ReactNode }) => (
      <ul className="m-0 pl-5 list-disc">{children}</ul>
    ),
    li: ({ children }: { children: React.ReactNode }) => (
      <li className="m-0 my-0.5">{children}</li>
    ),
  } as const;

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-3xl px-4 py-3 rounded-card shadow-soft ${
          isUser
            ? 'bg-rm-primary-700 text-white ml-2 sm:ml-8 md:ml-12'
            : isAssistant
            ? 'bg-rm-bg-50 text-rm-ink-900 mr-2 sm:mr-8 md:mr-12 border border-rm-neutral-600/10'
            : 'bg-rm-bg-tint text-rm-primary-700 border border-rm-accent-300/30 mx-2 sm:mx-8 md:mx-12'
        }`}
      >
        {isSystem && (
          <div className="text-xs font-medium mb-1 opacity-75 text-rm-primary-700">System</div>
        )}
        
        <div 
          // Only preserve whitespace for non-Markdown content; let Markdown handle spacing
          className={`${isAssistant ? '' : 'whitespace-pre-wrap'} break-words [&>p:empty]:hidden [&>p]:m-0 [&>p]:leading-relaxed [&>p+p]:mt-1 [&>p+ol]:mt-3 [&>p+ul]:mt-3`} 
          style={{ overflowWrap: 'anywhere' }}
        >
          {isAssistant ? (
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {processContentWithCitations(displayContent || (isLoading ? 'Typing...' : ''))}
            </ReactMarkdown>
          ) : (
            displayContent || (isLoading ? 'Typing...' : '')
          )}
        </div>

        {/* Show citations for assistant messages */}
        {isAssistant && citations.length > 0 && (
          <Citations 
            citations={citations} 
            annotations={annotations}
            messageContent={displayContent}
            sessionId={currentSessionId || undefined}
          />
        )}

        {/* Show booking button if wellbeing officer email or phone is mentioned */}
        {shouldShowBookingButton && (
          <div className="mt-3 pt-3 border-t border-rm-neutral-600/10">
            <button
              onClick={handleBookingClick}
              className="inline-flex items-center gap-2 px-4 py-2 bg-rm-accent-600 hover:bg-rm-accent-500 text-white rounded-lg font-medium transition-colors shadow-soft"
              aria-label="Book a meeting"
            >
              <svg 
                className="w-4 h-4" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" 
                />
              </svg>
              Book a meeting
            </button>
          </div>
        )}
        
        {isAssistant && message.timestamp && (
          <div className="text-xs mt-2 opacity-70 text-rm-neutral-600">
            {(() => {
              const messageDate = new Date(message.timestamp);
              const today = new Date();
              const isToday = messageDate.toDateString() === today.toDateString();
              
              if (isToday) {
                return messageDate.toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit' });
              } else {
                return messageDate.toLocaleString([], { 
                  year: 'numeric', 
                  month: 'short', 
                  day: 'numeric',
                  hour12: false, 
                  hour: '2-digit', 
                  minute: '2-digit' 
                });
              }
            })()}
          </div>
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Detailed comparison to prevent unnecessary re-renders
  const messageUnchanged = 
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.content === nextProps.message.content &&
    prevProps.message.fullContent === nextProps.message.fullContent &&
    prevProps.message.timestamp === nextProps.message.timestamp &&
    prevProps.message.role === nextProps.message.role &&
    prevProps.message.isStreaming === nextProps.message.isStreaming &&
    prevProps.message.status === nextProps.message.status;

  const propsUnchanged = 
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.currentSessionId === nextProps.currentSessionId;

  // Deep compare annotations and citations if they exist
  const annotationsUnchanged = JSON.stringify(prevProps.message.getAnnotations()) === 
    JSON.stringify(nextProps.message.getAnnotations());
  
  const citationsUnchanged = JSON.stringify(prevProps.message.getCitations()) === 
    JSON.stringify(nextProps.message.getCitations());

  return messageUnchanged && propsUnchanged && annotationsUnchanged && citationsUnchanged;
});
